import * as React from 'react'

import config from '@app/config'
import Theme from '@theme/Theme'
import styled from '@theme/styled-components'

import Loader from 'react-loader-spinner'
import GoogleMap from 'google-map-react'

import MarkerOrphan from '@components/marker/orphan'
import { MdLocationOff } from 'react-icons/md'
import useI18n from '@store/i18n/useI18n'

interface Props {
  navigation: Navigation
  screenStatus: ScreenStatus
  orphan: OrphanDeclarationDetails | undefined
}

const OrphanFocusMap = ({ screenStatus, orphan }: Props) => {
  const i18n = useI18n()
  const mapRef = React.useRef()

  React.useEffect(() => {
    if (orphan && orphan.location.latitude && orphan.location.longitude) {
      const map: any = mapRef.current
      map && map.setZoom(19)
      map && map.panTo({ lat: orphan.location.latitude, lng: orphan.location.longitude })
    }
  }, [orphan])

  return (
    <Container id="map-div">
      <GoogleMap
        bootstrapURLKeys={{ key: config.GMAP_APIKEY }}
        defaultCenter={{
          lat: 47.09,
          lng: 2.25,
        }}
        defaultZoom={6}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map
        }}
        options={{ fullscreenControl: false }}>
        {screenStatus === 'loading' ? (
          <StatusContainer>
            <Loader type="ThreeDots" color={Theme.colors.mapButtonLoader} height={30} />
          </StatusContainer>
        ) : !orphan || !orphan.location.latitude || !orphan.location.longitude ? (
          <NoLocationContainer>
            <NoLocationTitleContainer>
              <NoLocationIcon>
                <MdLocationOff size={30} color={Theme.colors.noLocationIcon} />
              </NoLocationIcon>
              <NoLocationTitle>{i18n.t('components.businessFocus.noLocationTitle')}</NoLocationTitle>
            </NoLocationTitleContainer>
            <NoLocationDescription>
              {!orphan
                ? i18n.t('components.businessFocus.noLocationErrorDescription')
                : i18n.t('components.businessFocus.noLocationDataDescription')}
            </NoLocationDescription>
          </NoLocationContainer>
        ) : (
          <MarkerOrphan
            size={60}
            key={orphan.id}
            lat={orphan.location.latitude}
            lng={orphan.location.longitude}
            onClick={() => {
              const map: any = mapRef.current
              map && map.setZoom(19)
              map && map.panTo({ lat: orphan.location.latitude, lng: orphan.location.longitude })
            }}
            focused={true}
            photoCount={orphan.pictures.length}
          />
        )}
      </GoogleMap>
    </Container>
  )
}

export default OrphanFocusMap

const Container = styled('div')`
  display: flex;
  flex: 1;
`
const StatusContainer = styled('div')`
  position: absolute;
`
const NoLocationContainer = styled('div')`
  height: 200px;
  width: 350px;
  margin-left: -175px;
  margin-top: -100px;
  background-color: white;
  border-radius: 15px;
  box-shadow: 0px 0px 10px ${(props) => props.theme.colors.actionButtonshadow};
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`
const NoLocationTitleContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
`
const NoLocationIcon = styled('div')`
  height: 50px;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 5px;
`
const NoLocationTitle = styled('div')`
  margin-left: 10px;
  ${(props) => props.theme.fonts.noLocationTitle};
`
const NoLocationDescription = styled('div')`
  ${(props) => props.theme.fonts.noLocationDescription};
  text-align: center;
  margin: 0 75px;
`
